import { AddressIdentity } from "@/components/AddressIdentity"
import { DialogButton } from "@/components/DialogButton"
import { accountStatus$, selectedAccountAddr$ } from "@/state/account"
import { lastReward$ } from "@/state/nominate"
import { useStateObservable } from "@react-rxjs/core"
import type { FC } from "react"
import { merge } from "rxjs"
import { NominateLocks, nominateLocksSub$ } from "./NominateLocks"
import { StopNominating } from "./StopNominating"
import { useNavigate, useParams } from "react-router"
import { Button } from "@polkahub/ui-components"
import { Badge } from "@/components/ui/badge"
import { Target } from "lucide-react"
import { cn } from "@/lib/utils"
import { LastEraReward } from "@/components/LastEraReward"

export const NominateStatus = () => {
  const accountStatus = useStateObservable(accountStatus$)!
  const lastEraReward = useStateObservable(lastReward$)
  const { chain } = useParams()
  const navigate = useNavigate()

  const isActive =
    accountStatus.nomination.currentBond > 0 &&
    (accountStatus.nomination.nominating?.validators || []).length > 0
  const badgeClassName = isActive
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800"
    : "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800"

  return (
    <div className="flex flex-col gap-6 grow ">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "h-2 w-2 rounded-full bg-emerald-500",
            isActive ? "animate-pulse" : "bg-gray-400",
          )}
        />
        <Badge
          variant="outline"
          className={cn("text-base font-semibold px-3 py-1 ", badgeClassName)}
        >
          Currently {isActive ? "nominating" : "inactive"}
        </Badge>
      </div>

      {isActive && (
        <div className="grid gap-4 md:grid-cols-2">
          <LastEraReward {...lastEraReward} />
          <div className="space-y-2 p-4 rounded-lg bg-muted/50 border border-border/50">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              <span>Reward destination</span>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-foreground">
                <Payee />
              </p>
            </div>
          </div>
        </div>
      )}

      {accountStatus.nomination.unlocks.length ? <NominateLocks /> : null}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button
          className="flex-1"
          onClick={() => navigate(`/${chain}/nominate/config`)}
        >
          Manage nomination
        </Button>
        {isActive ? (
          <DialogButton
            title="Stop nominating"
            className="flex-1"
            content={({ close }) => <StopNominating close={close} />}
            needsSigner
          >
            Stop nominating
          </DialogButton>
        ) : null}
      </div>
    </div>
  )
}

const Payee: FC = () => {
  const accountStatus = useStateObservable(accountStatus$)!
  const selectedAddr = useStateObservable(selectedAccountAddr$)!
  const payee = accountStatus.nomination.payee

  if (!payee) return "N/A"

  switch (payee.type) {
    case "Staked":
      return "Compounding"
    case "Stash":
      return "Your account"
    case "Controller":
      return (
        <AddressIdentity
          addr={accountStatus.nomination.controller ?? selectedAddr}
        />
      )
    case "Account":
      return <AddressIdentity addr={payee.value} />
    case "None":
      return "Discarded"
  }
}

export const nominateStatusSub$ = merge(
  lastReward$,
  accountStatus$,
  selectedAccountAddr$,
  nominateLocksSub$,
)
